<!-- Component can be used in microscope and periscope -->
<template>
  <div class="label-and-hint">
    <component
      :is="labelAsSpan ? 'span' : 'label'"
      v-if="label"
      :id="id"
      class="label"
      :class="{ disabled }"
      :for="!labelAsSpan ? inputId : undefined"
    >
      {{ label }}
    </component>
    <slot :input-id="inputId" :ariaLabelledby="id" :is-success="isSuccess" :is-warning="isWarning" :is-err="isErr" />

    <div
      v-if="!hideDetails && message"
      class="hint"
      :class="{
        disabled: disabled,
        emphasis: isSuccess || isWarning || isErr,
      }"
    >
      <CheckboxErrorFill v-if="isErr" />
      <CheckboxWarningFill v-else-if="isWarning" />
      <CheckboxSuccessFill v-else-if="isSuccess" />
      {{ message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import CheckboxSuccessFill from '../icons/CheckboxSuccessFill.vue'
import CheckboxWarningFill from '../icons/CheckboxWarningFill.vue'
import CheckboxErrorFill from '../icons/CheckboxErrorFill.vue'
import { computed } from 'vue'

interface LabelAndHintProps {
  id?: string | number
  modelValue?: unknown
  label?: string
  labelAsSpan?: boolean
  placeholder?: string

  hint?: string | string[]
  hideDetails?: boolean

  success?: boolean
  successMessages?: string | string[]

  warning?: boolean
  warningMessages?: string | string[]

  error?: boolean
  errorMessages?: string | string[]

  disabled?: boolean
}

const props = defineProps<LabelAndHintProps>()

const getMessage = (msg?: string | string[]) => {
  if (typeof msg === 'string') {
    return msg
  }

  if (Array.isArray(msg) && msg.length) {
    return [...msg].shift()
  }
}

const isSuccess = computed(() => props.success || !!getMessage(props.successMessages))
const isWarning = computed(() => props.warning || !!getMessage(props.warningMessages))
const isErr = computed(() => props.error || !!getMessage(props.errorMessages))

const message = computed(
  () =>
    getMessage(props.errorMessages) ??
    getMessage(props.warningMessages) ??
    getMessage(props.successMessages) ??
    getMessage(props.hint),
)

const id = computed(() => (props.id ? props.id.toString() : Math.random().toString().substring(2)))
const inputId = computed(() => id.value + '-input')
</script>

<style lang="scss" scoped>
.label-and-hint {
  width: 100%;

  > label,
  span.label {
    display: inline-block;
    margin-bottom: 4px;
    font-size: 16px;
  }

  > div.hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    font-size: 14px;
    font-weight: 400;
    color: var(--color-fg-subtle);

    &.emphasis {
      color: var(--color-fg-default);
    }

    &.disabled {
      color: var(--color-fg-disabled);
    }
  }
}
</style>
